/**
 * batch-62 uitrusting-inkoop (FO-466 + FO-467) — gedeelde types, labels en
 * Zod-schema's voor:
 *  - FO-466: pakbon-/factuurregels van uitrustingsleveranciers (Pehavo, Van
 *    Ysselt) koppelen aan de uitrusting van een medewerker, met dubbel-controle;
 *  - FO-467: "Al geleverd - verwerken zonder bestelling" (derde leverwijze),
 *    telverschillen, dubbele aanvragen en de opschoonlijst.
 *
 * Eén gedeeld schema per mutatie-endpoint (CLAUDE.md § Validatie): de API
 * parseert ermee en de backoffice valideert er client-side mee. Redenen zijn
 * server-side verplicht (spec 167 §5.1). Bewust geen import van andere
 * uitrusting-type-bestanden, zodat er geen import-cirkel ontstaat.
 */
import { z } from 'zod'
import { MAAT_TYPES } from './uitrusting-credits-sync'

// ─── Enums (spiegelen de Prisma-enums) ──────────────────────────────────────

export const UITRUSTING_INKOOP_BRONNEN = ['FACTUUR', 'PAKBON'] as const
export type UitrustingInkoopBron = (typeof UITRUSTING_INKOOP_BRONNEN)[number]

export const UITRUSTING_INKOOP_BRON_LABELS: Record<UitrustingInkoopBron, string> = {
  FACTUUR: 'Factuur',
  PAKBON: 'Pakbon',
}

export const UITRUSTING_KOPPEL_ACTIES = [
  'UITGIFTE',
  'LEVERING_BESTELLING',
  'ALLEEN_KOPPELEN',
  'NAAR_VOORRAAD',
  'OVERGESLAGEN',
  'RETOUR',
] as const
export type UitrustingKoppelActie = (typeof UITRUSTING_KOPPEL_ACTIES)[number]

export const UITRUSTING_KOPPEL_ACTIE_LABELS: Record<UitrustingKoppelActie, string> = {
  UITGIFTE: 'Toevoegen aan uitrusting',
  LEVERING_BESTELLING: 'Levering van bestelling registreren',
  ALLEEN_KOPPELEN: 'Alleen koppelen aan bestaande uitgifte',
  NAAR_VOORRAAD: 'Naar voorraad (ontvangst registreren)',
  OVERGESLAGEN: 'Overslaan',
  RETOUR: 'Retour: uitgifte terugdraaien',
}

export const UITRUSTING_KOPPEL_STATUSSEN = ['VOORSTEL', 'BEVESTIGD', 'TERUGGEDRAAID'] as const
export type UitrustingKoppelStatus = (typeof UITRUSTING_KOPPEL_STATUSSEN)[number]

export const UITRUSTING_KOPPEL_STATUS_LABELS: Record<UitrustingKoppelStatus, string> = {
  VOORSTEL: 'Te beoordelen',
  BEVESTIGD: 'Verwerkt',
  TERUGGEDRAAID: 'Teruggedraaid',
}

export const UITRUSTING_PAKBON_STATUSSEN = ['TE_VERWERKEN', 'VERWERKT', 'GEANNULEERD'] as const
export type UitrustingPakbonStatus = (typeof UITRUSTING_PAKBON_STATUSSEN)[number]

export const UITRUSTING_PAKBON_STATUS_LABELS: Record<UitrustingPakbonStatus, string> = {
  TE_VERWERKEN: 'Te verwerken',
  VERWERKT: 'Verwerkt',
  GEANNULEERD: 'Geannuleerd',
}

export const VOORRAAD_VERSCHIL_STATUSSEN = ['OPEN', 'AFGEWIKKELD'] as const
export type VoorraadVerschilStatus = (typeof VOORRAAD_VERSCHIL_STATUSSEN)[number]

export const VOORRAAD_VERSCHIL_STATUS_LABELS: Record<VoorraadVerschilStatus, string> = {
  OPEN: 'Open',
  AFGEWIKKELD: 'Afgewikkeld',
}

export const VOORRAAD_VERSCHIL_AFWIKKELINGEN = ['ONTVANGST', 'TELLING', 'RETOUR', 'HANDMATIG'] as const
export type VoorraadVerschilAfwikkeling = (typeof VOORRAAD_VERSCHIL_AFWIKKELINGEN)[number]

export const VOORRAAD_VERSCHIL_AFWIKKELING_LABELS: Record<VoorraadVerschilAfwikkeling, string> = {
  ONTVANGST: 'Meegenomen bij ontvangst',
  TELLING: 'Meegenomen in telling',
  RETOUR: 'Retour ontvangen',
  HANDMATIG: 'Handmatig afgewikkeld',
}

/**
 * Voorraadgevolg van een "al geleverde" regel (FO-467):
 *  - GEEN_MUTATIE: stand blijft, telverschil voor de eerstvolgende telling;
 *  - AFBOEKEN: na "eerst ontvangst registreren" afboeken tot wat de stand toelaat;
 *  - AL_GETELD: de voorraad is ná de fysieke uitgifte al geteld/bijgewerkt. De
 *    stand klopt dus al: geen mutatie én geen telverschil (anders trekt de
 *    volgende telling de uitgifte er nog een keer af).
 *  - BUITEN_MAGAZIJN: het kwam niet uit het STREEP-magazijn (de leverancier
 *    leverde direct aan de medewerker, of het is lokaal gekocht). Geen mutatie,
 *    geen telverschil, en ook bruikbaar zonder voorraadkoppeling of met genoeg
 *    vrije voorraad.
 */
export const UITRUSTING_VOORRAADGEVOLGEN = ['GEEN_MUTATIE', 'AFBOEKEN', 'AL_GETELD', 'BUITEN_MAGAZIJN'] as const
export type UitrustingVoorraadgevolg = (typeof UITRUSTING_VOORRAADGEVOLGEN)[number]

export const UITRUSTING_VOORRAADGEVOLG_LABELS: Record<UitrustingVoorraadgevolg, string> = {
  GEEN_MUTATIE: 'Geen mutatie (telverschil registreren)',
  AFBOEKEN: 'Eerst ontvangst registreren, daarna afboeken',
  AL_GETELD: 'Voorraad al bijgewerkt (geteld na de uitgifte)',
  BUITEN_MAGAZIJN: 'Niet uit het magazijn (extern geleverd of lokaal gekocht)',
}

export const UITRUSTING_ZONDER_BESTELLING_BRONNEN = ['VERWERKEN', 'OPSCHONING'] as const
export type UitrustingZonderBestellingBron = (typeof UITRUSTING_ZONDER_BESTELLING_BRONNEN)[number]

/** Soorten bestaande uitgifte waar een pakbon-/factuurregel aan kan hangen. */
export const UITGIFTE_REF_SOORTEN = [
  'BESTELREGEL',
  'UITRUSTING_ITEM',
  'VOORRAAD_MUTATIE',
  'UNIEK_ITEM',
  'KOPPELING',
] as const
export type UitgifteRefSoort = (typeof UITGIFTE_REF_SOORTEN)[number]

export const UITGIFTE_REF_SOORT_LABELS: Record<UitgifteRefSoort, string> = {
  BESTELREGEL: 'Bestelling',
  UITRUSTING_ITEM: 'Uitrusting',
  VOORRAAD_MUTATIE: 'Uitgifte uit voorraad',
  UNIEK_ITEM: 'Exemplaar',
  KOPPELING: 'Pakbon/factuur',
}

export type UitrustingMatchStatus = 'EENDUIDIG' | 'MEERDERE' | 'GEEN'
export type UitrustingMedewerkerMatch = UitrustingMatchStatus | 'GEEN_NAAM'

/** Minimale lengte van een verplichte reden (spec 167 §5.1: "min. enkele tekens"). */
export const UITRUSTING_REDEN_MIN = 5

// ─── DTO's: koppel-paneel (FO-466) ──────────────────────────────────────────

export interface UitrustingMedewerkerRefDTO {
  id: string
  naam: string
  employeeNumber: string | null
}

/** Een catalogus-variant als keuze in de review. */
export interface UitrustingArtikelKeuzeDTO {
  artikelId: string
  variantId: string
  naam: string
  maat: string | null
  maatType: string | null
  artikelnummer: string
  // batch-69 samenstellen (FO-510): ook MERCHANDISE (spiegel van UitrustingCategorie).
  categorie: 'KLEDING' | 'GEREEDSCHAP' | 'PBM' | 'VEILIGHEID' | 'MERCHANDISE'
  /** Voorraad-bron van deze maat (FO-419), of null. */
  voorraadArtikelId: string | null
  creditsPerStuk: number
}

/** Een bestaande uitgifte op de medewerker (dubbel-treffer, retour-kandidaat, doel). */
export interface UitrustingUitgifteRefDTO {
  soort: UitgifteRefSoort
  id: string
  datum: string | null
  artikelNaam: string
  maat: string | null
  aantal: number
  /** Leesbare herkomst: "Bestelling van 12-08-2026", "Pakbon 88231 (Van Ysselt)". */
  herkomst: string
  /** Koppeling waar deze uitgifte uit voortkwam (pakbon/factuur), of null. */
  koppelingId: string | null
  /** De uitgifte heeft zelf al credits gekost (bestelling of Van Ysselt-afname). */
  creditsGedragen: boolean
}

/** Een lopende bestelregel van dezelfde medewerker + hetzelfde artikel. */
export interface UitrustingOpenBestelregelDTO {
  bestellingId: string
  regelId: string
  maat: string | null
  aantalOpen: number
  datum: string | null
  /** De bestelde maat wijkt af van de maat op de pakbon/factuur. */
  maatWijktAf: boolean
  /** Goedgekeurd maar nog niet verwerkt: eerst verwerken, dan de levering registreren. */
  nogTeVerwerken: boolean
}

/** Credits die een nieuwe uitgifte zonder bestelling zou afboeken (Van Ysselt). */
export interface UitrustingKoppelCreditsDTO {
  vanYsselt: boolean
  creditVrij: boolean
  creditsPerStuk: number
  totaal: number
  jaar: number
  kwartaal: number
}

export interface UitrustingKoppelRegelDTO {
  koppelingId: string
  bron: UitrustingInkoopBron
  bronRegelId: string | null
  regelPositie: number
  omschrijving: string
  sku: string | null
  maat: string | null
  aantal: number
  /** Regelbedrag excl. btw (factuur), of null (pakbon zonder prijzen). */
  bedrag: number | null
  persoonsnaam: string | null
  leverdatum: string
  status: UitrustingKoppelStatus
  /** De bronregel is na bevestigen gewijzigd of verdwenen: opnieuw beoordelen. */
  bronregelGewijzigd: boolean
  isCreditregel: boolean

  // ── Analyse ──
  voorgesteldeActie: UitrustingKoppelActie | null
  medewerkerMatch: UitrustingMedewerkerMatch
  voorgesteldeMedewerker: UitrustingMedewerkerRefDTO | null
  medewerkerKandidaten: UitrustingMedewerkerRefDTO[]
  artikelMatch: UitrustingMatchStatus
  artikelMatchGrond: 'SKU' | 'NAAM' | null
  voorgesteldArtikel: UitrustingArtikelKeuzeDTO | null
  artikelSuggesties: UitrustingArtikelKeuzeDTO[]
  dubbelTreffers: UitrustingUitgifteRefDTO[]
  openBestelregels: UitrustingOpenBestelregelDTO[]
  retourKandidaten: UitrustingUitgifteRefDTO[]
  /** Regel-maat wijkt af van de geregistreerde maat van de medewerker. */
  maatAfwijking: { geregistreerd: string; regel: string } | null
  credits: UitrustingKoppelCreditsDTO | null
  waarschuwingen: string[]

  // ── Besluit ──
  actie: UitrustingKoppelActie | null
  medewerker: UitrustingMedewerkerRefDTO | null
  artikel: UitrustingArtikelKeuzeDTO | null
  /** De uitgifte die dit opleverde of waar het aan hangt. */
  uitgifte: UitrustingUitgifteRefDTO | null
  creditsAfgeboekt: number
  reden: string | null
  bevestigdOp: string | null
  bevestigdDoorNaam: string | null
  teruggedraaidOp: string | null
  terugdraaiReden: string | null
}

export interface UitrustingKoppelSamenvattingDTO {
  totaal: number
  teBeoordelen: number
  verwerkt: number
  dubbelVerdacht: number
  nietHerkend: number
  zonderPersoon: number
  bronregelGewijzigd: number
}

export interface UitrustingGekoppeldDocumentDTO {
  bron: UitrustingInkoopBron
  id: string
  nummer: string
  datum: string
}

export interface UitrustingKoppelPaneelDTO {
  bron: UitrustingInkoopBron
  documentId: string
  documentNummer: string
  leverancierId: string
  leverancierNaam: string
  leverdatum: string
  /** De leverancier is een uitrustingsleverancier (Pehavo, Van Ysselt, ...). */
  isUitrustingsleverancier: boolean
  isVanYsselt: boolean
  venster: { dagenVoor: number; dagenNa: number }
  regels: UitrustingKoppelRegelDTO[]
  samenvatting: UitrustingKoppelSamenvattingDTO
  /** FACTUUR: de pakbonnen van dezelfde levering; PAKBON: de factuur. */
  gekoppeldeDocumenten: UitrustingGekoppeldDocumentDTO[]
}

export interface UitrustingKoppelBevestigResultaatDTO {
  resultaten: Array<{
    koppelingId: string
    ok: boolean
    status: UitrustingKoppelStatus | null
    melding: string | null
    waarschuwingen: string[]
  }>
}

// ─── DTO's: pakbonnen ───────────────────────────────────────────────────────

export interface UitrustingPakbonRegelDTO {
  id: string
  position: number
  omschrijving: string
  sku: string | null
  maat: string | null
  aantal: number
  persoonsnaam: string | null
  bedrag: number | null
}

export interface UitrustingPakbonListRowDTO {
  id: string
  pakbonnummer: string
  leverancierId: string
  leverancierNaam: string
  leverdatum: string
  status: UitrustingPakbonStatus
  aantalRegels: number
  teBeoordelen: number
  factuurId: string | null
  factuurNr: string | null
  heeftPdf: boolean
  createdAt: string
}

export interface UitrustingPakbonAuditDTO {
  id: string
  actie: string
  actorNaam: string | null
  toelichting: string | null
  createdAt: string
}

export interface UitrustingPakbonDetailDTO extends UitrustingPakbonListRowDTO {
  referentie: string | null
  markeringen: string[]
  regels: UitrustingPakbonRegelDTO[]
  /** De laatste 50 audit-regels; `auditsTotaal` is de echte telling. */
  audits: UitrustingPakbonAuditDTO[]
  auditsTotaal: number
}

// ─── DTO's: werkvoorraad, herkomst ──────────────────────────────────────────

export interface UitrustingInkoopWerkvoorraadRijDTO {
  bron: UitrustingInkoopBron
  documentId: string
  documentNummer: string
  leverancierNaam: string
  datum: string
  aantalRegels: number
  teBeoordelen: number
  dubbelVerdacht: number
  nietHerkend: number
  zonderPersoon: number
  bronregelGewijzigd: number
}

export interface UitrustingInkoopWerkvoorraadMetaDTO {
  total: number
  page: number
  pageSize: number
  /** Tellers over de VOLLEDIGE gefilterde set (server-side), niet over de pagina. */
  totaalTeBeoordelen: number
  totaalDubbelVerdacht: number
  totaalNietHerkend: number
  totaalZonderPersoon: number
}

/** Herkomst van één stuk uitrusting (profiel): pakbon/factuur, datum, leverancier, bedrag. */
export interface UitrustingHerkomstDTO {
  koppelingId: string
  bron: UitrustingInkoopBron
  documentId: string
  documentNummer: string
  leverancierNaam: string
  datum: string
  /** Regelbedrag; null voor wie geen kosten mag zien of bij een pakbon zonder prijs. */
  bedrag: number | null
  /** Relatief backoffice-pad naar het document (same-origin). */
  href: string
}

/** Herkomst per rij van de Middelen-tab van één medewerker. */
export interface UitrustingHerkomstOverzichtDTO {
  /** UitrustingItem-id (stuk bezit) -> herkomst. */
  items: Record<string, UitrustingHerkomstDTO[]>
  /** VoorraadUniekItem-id (exemplaar) -> herkomst. */
  uniekItems: Record<string, UitrustingHerkomstDTO[]>
  /** Voorraad-artikel-id (uitgifte uit voorraad, per artikel getoond) -> herkomst. */
  bulkArtikelen: Record<string, UitrustingHerkomstDTO[]>
}

export const UITRUSTING_INKOOP_WERKVOORRAAD_FILTERS = [
  'TE_BEOORDELEN',
  'DUBBEL',
  'NIET_HERKEND',
  'ZONDER_PERSOON',
  'GEWIJZIGD',
] as const
export type UitrustingInkoopWerkvoorraadFilter = (typeof UITRUSTING_INKOOP_WERKVOORRAAD_FILTERS)[number]

export const UITRUSTING_INKOOP_WERKVOORRAAD_FILTER_LABELS: Record<UitrustingInkoopWerkvoorraadFilter, string> = {
  TE_BEOORDELEN: 'Te beoordelen',
  DUBBEL: 'Mogelijk dubbel',
  NIET_HERKEND: 'Artikel niet herkend',
  ZONDER_PERSOON: 'Zonder persoon',
  GEWIJZIGD: 'Bronregel gewijzigd',
}

// ─── DTO's: FO-467 ──────────────────────────────────────────────────────────

export interface VoorraadVerschilDTO {
  id: string
  voorraadArtikelId: string | null
  artikelNaam: string
  aantal: number
  /** "uitgegeven zonder registratie - telverschil {n} stuks". */
  omschrijving: string
  status: VoorraadVerschilStatus
  employeeId: string
  employeeNaam: string
  bestellingId: string | null
  reden: string
  geregistreerdDoorNaam: string | null
  exemplaarOnbekend: boolean
  createdAt: string
  afgewikkeldOp: string | null
  afgewikkeldDoorNaam: string | null
  afwikkeling: VoorraadVerschilAfwikkeling | null
  afwikkelReden: string | null
}

/** Een regel die als "Al geleverd - verwerken zonder bestelling" is afgehandeld. */
export interface ZonderBestellingInfoDTO {
  bron: UitrustingZonderBestellingBron
  voorraadgevolg: UitrustingVoorraadgevolg
  aantalAfgeboekt: number
  aantalZonderMutatie: number
  reden: string
  actorNaam: string | null
  datum: string
  exemplaarOnbekend: boolean
  verschil: { id: string; status: VoorraadVerschilStatus; aantal: number } | null
  /** Bewust geen credits afgeschreven (met reden); `creditsTerug` = wat terugging. */
  geenCredits: boolean
  geenCreditsReden: string | null
  creditsTerug: number
}

/** Een andere open aanvraag van dezelfde medewerker voor hetzelfde artikel. */
export interface DubbeleAanvraagDTO {
  bestellingId: string
  regelId: string
  bestellingStatus: string
  regelStatus: string
  datum: string | null
  aantal: number
  maat: string | null
  artikelNaam: string
}

export const OPSCHOON_VOORSTELLEN = ['VERWERKEN_ZONDER_BESTELLING', 'SLUITEN_ALS_DUBBEL'] as const
export type OpschoonVoorstel = (typeof OPSCHOON_VOORSTELLEN)[number]

export const OPSCHOON_VOORSTEL_LABELS: Record<OpschoonVoorstel, string> = {
  VERWERKEN_ZONDER_BESTELLING: 'Verwerken zonder bestelling',
  SLUITEN_ALS_DUBBEL: 'Sluiten als dubbele aanvraag (credits terug)',
}

export interface OpschoonKandidaatDTO {
  bestellingId: string
  regelId: string
  bestellingStatus: string
  employeeId: string
  employeeNaam: string
  artikelNaam: string
  maat: string | null
  aantal: number
  datum: string | null
  bewijs: UitrustingUitgifteRefDTO
  voorstel: OpschoonVoorstel
  waarschuwing: string | null
  /** Niet bulk-verwerkbaar (bv. deels geleverd): alleen handmatig. */
  blokkade: string | null
}

export interface OpschoonRapportRegelDTO {
  regelId: string
  bestellingId: string
  /** Leesbaar: "Medewerker - artikel (maat) x aantal", of null als de regel onbekend is. */
  label: string | null
  uitkomst: 'VERWERKT_ZONDER_BESTELLING' | 'GESLOTEN_ALS_DUBBEL' | 'OVERGESLAGEN'
  melding: string | null
}

export interface OpschoonRapportDTO {
  verwerkt: number
  gesloten: number
  overgeslagen: number
  regels: OpschoonRapportRegelDTO[]
}

// ─── Zod: koppelen (FO-466) ─────────────────────────────────────────────────

const idVeld = () => z.string().min(1).max(50)

export const UitgifteDoelSchema = z.object({
  soort: z.enum(UITGIFTE_REF_SOORTEN),
  id: idVeld(),
})
export type UitgifteDoel = z.infer<typeof UitgifteDoelSchema>

export const UitrustingKoppelBevestigRegelSchema = z
  .object({
    koppelingId: idVeld(),
    actie: z.enum(UITRUSTING_KOPPEL_ACTIES),
    employeeId: idVeld().nullable().optional(),
    variantId: idVeld().nullable().optional(),
    /** LEVERING_BESTELLING: de bestelregel waarvan dit de levering is. */
    bestellingRegelId: idVeld().nullable().optional(),
    /** ALLEEN_KOPPELEN / RETOUR: de bestaande uitgifte. */
    doelUitgifte: UitgifteDoelSchema.nullable().optional(),
    /** NAAR_VOORRAAD: de voorraadlocatie van de ontvangst. */
    locatieId: idVeld().nullable().optional(),
    /** NAAR_VOORRAAD: openstaande telverschillen die deze ontvangst afwikkelt (FO-467). */
    afwikkelVerschilIds: z.array(idVeld()).max(100).optional(),
    /** UITGIFTE ondanks een dubbel-treffer ("toch toevoegen"): reden verplicht. */
    tochToevoegen: z.boolean().optional(),
    /** Van Ysselt-credits afboeken bij een nieuwe uitgifte (default aan). */
    creditsAfboeken: z.boolean().optional(),
    reden: z.string().trim().max(1000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    const reden = (v.reden ?? '').trim()
    const eis = (pad: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [pad], message })
    const metMedewerker: UitrustingKoppelActie[] = ['UITGIFTE', 'LEVERING_BESTELLING', 'ALLEEN_KOPPELEN']
    if (metMedewerker.includes(v.actie) && !v.employeeId) eis('employeeId', 'Kies de medewerker')
    if ((v.actie === 'UITGIFTE' || v.actie === 'NAAR_VOORRAAD') && !v.variantId) {
      eis('variantId', 'Kies het artikel en de maat')
    }
    if (v.actie === 'LEVERING_BESTELLING' && !v.bestellingRegelId) {
      eis('bestellingRegelId', 'Kies de bestelregel waarvan dit de levering is')
    }
    if ((v.actie === 'ALLEEN_KOPPELEN' || v.actie === 'RETOUR') && !v.doelUitgifte) {
      eis('doelUitgifte', 'Kies de bestaande uitgifte')
    }
    if (v.actie === 'NAAR_VOORRAAD' && !v.locatieId) eis('locatieId', 'Kies de voorraadlocatie')
    if (v.tochToevoegen && reden.length < UITRUSTING_REDEN_MIN) {
      eis('reden', 'Geef aan waarom er een tweede exemplaar bij komt (minimaal 5 tekens)')
    }
    if (v.creditsAfboeken === false && reden.length < UITRUSTING_REDEN_MIN) {
      eis('reden', 'Geef aan waarom er geen credits worden afgeboekt (minimaal 5 tekens)')
    }
  })
export type UitrustingKoppelBevestigRegelInput = z.infer<typeof UitrustingKoppelBevestigRegelSchema>

export const UitrustingKoppelBevestigSchema = z.object({
  regels: z.array(UitrustingKoppelBevestigRegelSchema).min(1, 'Kies minimaal één regel').max(200),
})
export type UitrustingKoppelBevestigInput = z.infer<typeof UitrustingKoppelBevestigSchema>

export const UitrustingKoppelTerugdraaiSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(UITRUSTING_REDEN_MIN, 'Geef een reden van minimaal 5 tekens')
    .max(1000),
})
export type UitrustingKoppelTerugdraaiInput = z.infer<typeof UitrustingKoppelTerugdraaiSchema>

// ─── Zod: pakbonnen ─────────────────────────────────────────────────────────

/**
 * yyyy-mm-dd, of de volledige ISO-tijd zoals de read-DTO hem teruggeeft (read <-> write
 * consistent); alleen de datum telt. De API slaat een pakbondatum op als 12:00 UTC, dus de
 * datum-prefix is altijd de NL-kalenderdag.
 */
const ISO_DATUM = /^\d{4}-\d{2}-\d{2}(?:T[\d:.]+(?:Z|[+-]\d{2}:\d{2})?)?$/
const datumVeld = () =>
  z
    .string()
    .regex(ISO_DATUM, 'Gebruik een geldige datum')
    .transform((s) => s.slice(0, 10))

export const UitrustingPakbonRegelInputSchema = z.object({
  omschrijving: z.string().trim().min(1, 'Omschrijving is verplicht').max(500),
  sku: z.string().trim().max(100).nullable().optional(),
  maat: z.string().trim().max(50).nullable().optional(),
  aantal: z
    .number()
    .int('Aantal is een heel getal')
    .refine((n) => n !== 0, 'Aantal mag niet 0 zijn')
    .refine((n) => Math.abs(n) <= 500, 'Aantal is te groot'),
  persoonsnaam: z.string().trim().max(200).nullable().optional(),
  bedrag: z.number().nullable().optional(),
})
export type UitrustingPakbonRegelInput = z.infer<typeof UitrustingPakbonRegelInputSchema>

export const UitrustingPakbonCreateSchema = z.object({
  leverancierId: idVeld(),
  pakbonnummer: z.string().trim().min(1, 'Pakbonnummer is verplicht').max(100),
  leverdatum: datumVeld(),
  referentie: z.string().trim().max(200).nullable().optional(),
  regels: z.array(UitrustingPakbonRegelInputSchema).min(1, 'Voeg minimaal één regel toe').max(500),
})
export type UitrustingPakbonCreateInput = z.infer<typeof UitrustingPakbonCreateSchema>

export const UitrustingPakbonUpdateSchema = z.object({
  pakbonnummer: z.string().trim().min(1, 'Pakbonnummer is verplicht').max(100).optional(),
  leverdatum: datumVeld().optional(),
  referentie: z.string().trim().max(200).nullable().optional(),
  /** Koppel aan de inkoopfactuur van dezelfde levering (null = ontkoppelen). */
  factuurId: idVeld().nullable().optional(),
})
export type UitrustingPakbonUpdateInput = z.infer<typeof UitrustingPakbonUpdateSchema>

export const UitrustingPakbonRegelUpdateSchema = UitrustingPakbonRegelInputSchema.partial()
export type UitrustingPakbonRegelUpdateInput = z.infer<typeof UitrustingPakbonRegelUpdateSchema>

export const UitrustingPakbonAnnuleerSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(UITRUSTING_REDEN_MIN, 'Geef een reden van minimaal 5 tekens')
    .max(1000),
})

// ─── Zod: instellingen ──────────────────────────────────────────────────────

export const UitrustingDubbelVensterSchema = z.object({
  dubbelVensterDagenVoor: z.number().int('Heel aantal dagen').min(0).max(365),
  dubbelVensterDagenNa: z.number().int('Heel aantal dagen').min(0).max(365),
})

// ─── Zod: FO-467 ────────────────────────────────────────────────────────────

/**
 * De derde leverwijze per bestelregel. Reden verplicht (bewuste afwijking,
 * spec 167 §2.1); het voorraadgevolg staat standaard op "geen mutatie".
 */
export const AlGeleverdKeuzeSchema = z
  .object({
    reden: z
      .string()
      .trim()
      .min(
        UITRUSTING_REDEN_MIN,
        'Geef een reden van minimaal 5 tekens (bijvoorbeeld: lag in het magazijn, voorraad nog niet ingeboekt)',
      )
      .max(1000),
    voorraadgevolg: z.enum(UITRUSTING_VOORRAADGEVOLGEN).default('GEEN_MUTATIE'),
    /** Traceerbaar artikel: de gescande exemplaren (rest = "exemplaar onbekend"). */
    uniekItemIds: z.array(idVeld()).max(100).optional(),
    /**
     * PO-besluit (2026-10): bij uitgifte wél credits afschrijven, tenzij anders
     * aangegeven. Standaard uit = de regel kost credits zoals bij goedkeuren is
     * afgeboekt (en Van Ysselt krijgt het saldo-bericht, FO-445). Aan = bewuste
     * uitzondering met eigen reden: de credits van deze regel gaan terug naar de
     * medewerker en er gaat geen Van Ysselt-deel uit.
     */
    geenCredits: z.boolean().default(false),
    geenCreditsReden: z.string().trim().max(1000).optional(),
    /**
     * batch-69 bestelflow (FO-512 §B.3) — het correctievoorstel bij "geen mutatie"
     * ("stand was 0, feitelijk minimaal n") meteen bevestigen: de API boekt dan
     * n stuks als correctie bij op deze locatie en geeft ze direct uit, zodat er
     * geen telverschil openblijft dat een latere ontvangst ten onrechte zou
     * verlagen. Alleen bij een niet-traceerbare bron en met magazijnrechten.
     */
    correctieBevestigen: z.object({ locatieId: idVeld() }).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.geenCredits && (val.geenCreditsReden ?? '').length < UITRUSTING_REDEN_MIN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['geenCreditsReden'],
        message:
          'Geef aan waarom er geen credits af gaan (minimaal 5 tekens, bijvoorbeeld: garantievervanging, betaald door de klant)',
      })
    }
  })
export type AlGeleverdKeuze = z.infer<typeof AlGeleverdKeuzeSchema>

export const SluitDubbeleAanvraagSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(UITRUSTING_REDEN_MIN, 'Geef een reden van minimaal 5 tekens')
    .max(1000),
  /** De aanvraag waarmee deze wordt samengevoegd (voor de audit), of null. */
  samengevoegdMetBestellingId: idVeld().nullable().optional(),
})
export type SluitDubbeleAanvraagInput = z.infer<typeof SluitDubbeleAanvraagSchema>

export const OpschoonVerwerkSchema = z.object({
  regelIds: z.array(idVeld()).min(1, 'Selecteer minimaal één regel').max(500),
  reden: z
    .string()
    .trim()
    .min(UITRUSTING_REDEN_MIN, 'Geef een reden van minimaal 5 tekens')
    .max(1000),
})
export type OpschoonVerwerkInput = z.infer<typeof OpschoonVerwerkSchema>

export const VoorraadVerschilAfwikkelSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(UITRUSTING_REDEN_MIN, 'Geef een reden van minimaal 5 tekens')
    .max(1000),
})
export type VoorraadVerschilAfwikkelInput = z.infer<typeof VoorraadVerschilAfwikkelSchema>

// ─── Review-aanvullingen (FO-466 §A.2, FO-467 §5.3 + §8) ───────────────────

/** Artikel aanmaken vanuit een niet-herkende pakbon-/factuurregel (voorgevuld uit de regel). */
export const UitrustingKoppelArtikelAanmakenSchema = z.object({
  naam: z.string().trim().min(2, 'Geef het artikel een naam').max(200),
  categorie: z.enum(['KLEDING', 'GEREEDSCHAP', 'PBM', 'VEILIGHEID']),
  artikelnummer: z.string().trim().min(1, 'Artikelnummer (SKU) is verplicht').max(100),
  maat: z.string().trim().max(50).nullable().optional(),
  /** Soort maat (MAAT_TYPES), of leeg bij een maatloos artikel. */
  maatType: z.enum(MAAT_TYPES).nullable().optional(),
  prijsExclBtw: z.number().min(0, 'De prijs kan niet negatief zijn').max(100_000),
  credits: z.number().int('Heel aantal credits').min(0).max(10_000),
})
export type UitrustingKoppelArtikelAanmakenInput = z.infer<typeof UitrustingKoppelArtikelAanmakenSchema>

export interface UitrustingKoppelArtikelAangemaaktDTO {
  artikelId: string
  variantId: string
}

/** Resultaat van terugdraaien, met waarschuwingen (bv. een intussen aangepaste kostenverdeling). */
export interface UitrustingKoppelTerugdraaiResultaatDTO {
  bron: UitrustingInkoopBron
  documentId: string
  waarschuwingen: string[]
}

/** Een exemplaar dat op voorraad ligt, te kiezen bij "al geleverd" van een traceerbaar artikel. */
export interface UitgifteExemplaarKeuzeDTO {
  id: string
  /** Leesbaar: serienummer / asset-tag / merk-model. */
  label: string
  serienummer: string | null
  assetTag: string | null
  locatieNaam: string | null
}

/** Openstaande telverschillen meenemen bij een ontvangst die elders is geboekt (bv. een inkooporder). */
export const VerschilMeenemenBijOntvangstSchema = z.object({
  locatieId: idVeld(),
  verschilIds: z.array(idVeld()).min(1, 'Kies minimaal één telverschil').max(100),
})
export type VerschilMeenemenBijOntvangstInput = z.infer<typeof VerschilMeenemenBijOntvangstSchema>
